// BOORO: the team area. Every office of the person with its groups and agents, as a canvas or as a list. Moving an agent or a group is a drag
// on the canvas and a choice in the list.
import type { OrgScope } from "@booro/team";
import { agentInScope, groupWithDescendants, orderedGroups } from "@booro/team";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { OrgAgent, OrgGroup, OrgSnapshot } from "@rakazo/contracts";
import { GROUP_COLORS, GROUP_NAME_MAX_LENGTH } from "@rakazo/contracts";
import {
  BotAvatar,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  NativeSelect,
  NativeSelectOption,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@rakazo/ui-web";
import { Building, Crown, Plus, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { AgentDetailPanel } from "./agent-detail";
import { officeRpc, openAgentChat, ScopeFilter, useOrgScope, useOrgSnapshot } from "./org-scope";
import type { TeamCanvasActions, TeamLens } from "./team-canvas";
import {
  AddMenu,
  AgentLightDot,
  GroupMenu,
  InlineName,
  OwnComputerMark,
  TeamCanvas,
} from "./team-canvas";
import type { WorkspaceChrome } from "./workspace-header";
import { WorkspaceViewHeader } from "./workspace-header";

type TeamMode = "canvas" | "list";
type OrgGroupColor = (typeof GROUP_COLORS)[number];
const MODE_STORAGE_KEY = "booro:team-mode";

/** The canvas on a wide screen, the list on a phone; a choice is remembered. */
function readTeamMode(): TeamMode {
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    if (stored === "canvas" || stored === "list") return stored;
  } catch {
    // Blocked storage falls back to the screen size.
  }
  return typeof window.matchMedia === "function" && window.matchMedia("(min-width: 768px)").matches
    ? "canvas"
    : "list";
}

function writeTeamMode(mode: TeamMode): void {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // The choice still holds until the page reloads.
  }
}

type GroupDialogState =
  | {
      mode: "create";
      spaceId: string;
      parentGroupId: string | null;
      /** Opened from the toolbar: the office is still to be chosen. */
      chooseOffice?: boolean;
    }
  | { mode: "agent"; spaceId: string; groupId: string | null; chooseOffice?: boolean }
  | { mode: "office" }
  | { mode: "lead" | "remove"; group: OrgGroup }
  | { mode: "officeLead"; spaceId: string }
  | null;

export function TeamView({ title, chrome }: { title: string; chrome: WorkspaceChrome }) {
  const { t } = useLingui();
  const { snapshot, error: loadError, reload } = useOrgSnapshot();
  const [scope, setScope] = useOrgScope();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState<TeamMode>(readTeamMode);
  const [lens, setLens] = useState<TeamLens>("status");
  const { pages, pageId, setPageId, readers } = useKnowledgeLens(lens === "knowledge");
  const [selected, setSelected] = useState<string | null>(null);
  const [dialog, setDialog] = useState<GroupDialogState>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  // An agent of another office opens after the app switched to that office.
  const detailParam = params.get("detail");
  useEffect(() => {
    if (!detailParam) return;
    setSelected(detailParam);
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("detail");
        return next;
      },
      { replace: true },
    );
  }, [detailParam, setParams]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(""), 5_000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const run = useCallback(
    async (change: () => Promise<unknown>) => {
      try {
        await change();
        setError("");
      } catch (cause) {
        setError(errorText(cause));
      } finally {
        await reload();
      }
    },
    [reload],
  );

  const openAgent = useCallback(
    (agent: OrgAgent) => {
      if (!snapshot) return;
      // The detail panel works in the office the app has open.
      if (agent.spaceId === snapshot.currentSpaceId) setSelected(agent.id);
      else openAgentChat(agent.spaceId, `/app/v/team?detail=${encodeURIComponent(agent.id)}`);
    },
    [snapshot],
  );

  const actions = useMemo<TeamCanvasActions>(
    () => ({
      openAgent,
      addGroup: (spaceId, parentGroupId) => setDialog({ mode: "create", spaceId, parentGroupId }),
      addAgent: (spaceId, groupId) => setDialog({ mode: "agent", spaceId, groupId }),
      editGroup: (group, editMode) => setDialog({ mode: editMode, group }),
      chooseOfficeLead: (spaceId) => setDialog({ mode: "officeLead", spaceId }),
      setLead: (agent, lead) =>
        void run(() =>
          agent.groupId
            ? rpc.booroOrg.updateGroup({
                groupId: agent.groupId,
                leadBotId: lead ? agent.id : null,
              })
            : rpc.booroOrg.leadOffice({
                spaceId: agent.spaceId,
                leadBotId: lead ? agent.id : null,
              }),
        ),
      setGroupColor: (group, color) =>
        void run(() =>
          rpc.booroOrg.updateGroup({
            groupId: group.id,
            color: color as OrgGroupColor | null,
          }),
        ),
      renameGroup: (group, name) =>
        run(() => rpc.booroOrg.updateGroup({ groupId: group.id, name })),
      setOfficeColor: (spaceId, color) =>
        void run(() => rpc.booroOrg.colorOffice({ spaceId, color: color as OrgGroupColor | null })),
      renameOffice: (spaceId, name) => run(() => rpc.spaces.rename({ spaceId, name })),
      // An agent is changed through its own office.
      renameAgent: (agent, name) =>
        run(() => officeRpc(agent.spaceId).bots.update({ botId: agent.id, name })),
      placeAgent: (agent, groupId) =>
        run(() => rpc.booroOrg.placeAgent({ botId: agent.id, groupId })),
      moveGroup: (group, parentGroupId, place) =>
        run(() => rpc.booroOrg.moveGroup({ groupId: group.id, parentGroupId, ...place })),
      placeOffice: (spaceId, place) => run(() => rpc.booroOrg.placeOffice({ spaceId, ...place })),
      keepPlaces: async ({ groups, offices }) => {
        await Promise.all([
          ...groups.map(({ group, x, y }) =>
            rpc.booroOrg.moveGroup({
              groupId: group.id,
              parentGroupId: group.parentGroupId,
              x: Math.round(x),
              y: Math.round(y),
            }),
          ),
          ...offices.map(({ spaceId, x, y }) =>
            rpc.booroOrg.placeOffice({ spaceId, x: Math.round(x), y: Math.round(y) }),
          ),
        ]).catch(() => undefined);
      },
      refuse: () =>
        setNotice(t`Agents and groups stay in their office, with their memory and access.`),
    }),
    [openAgent, run, t],
  );

  const agents = snapshot?.agents ?? [];
  const working = agents.filter((agent) => agent.light === "busy").length;
  const shownError = error || loadError;

  return (
    <>
      <WorkspaceViewHeader
        title={title}
        chrome={chrome}
        actions={
          <>
            <span className="hidden text-[13px] text-muted-foreground lg:inline">
              <Plural value={agents.length} one="# agent" other="# agents" />
              {working > 0 ? (
                <>
                  {" · "}
                  <Plural value={working} one="# working" other="# working" />
                </>
              ) : null}
            </span>
            {snapshot ? (
              <Tabs value={lens} onValueChange={(value) => setLens(value as TeamLens)}>
                <TabsList aria-label={t`Show`}>
                  <TabsTrigger value="status">
                    <Trans>Status</Trans>
                  </TabsTrigger>
                  <TabsTrigger value="knowledge">
                    <Trans>Knowledge</Trans>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
            {lens === "knowledge" ? (
              <NativeSelect
                aria-label={t`Page`}
                value={pageId}
                onChange={(event) => setPageId(event.target.value)}
              >
                <NativeSelectOption value="">{t`Choose a page`}</NativeSelectOption>
                {pages.map((page) => (
                  <NativeSelectOption key={page.id} value={page.id}>
                    {page.title}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            ) : null}
            <Tabs
              value={mode}
              onValueChange={(value) => {
                const next = value === "list" ? "list" : "canvas";
                setMode(next);
                writeTeamMode(next);
              }}
            >
              <TabsList aria-label={t`Layout`}>
                <TabsTrigger value="canvas">
                  <Trans>Canvas</Trans>
                </TabsTrigger>
                <TabsTrigger value="list">
                  <Trans>List</Trans>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </>
        }
      />
      {snapshot ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          <ScopeFilter snapshot={snapshot} scope={scope} onChange={setScope} />
          <div className="ms-auto flex items-center gap-2">
            {snapshot.chiefBotId ? null : (
              // The Chief leads every office; without one there is no top of the tree.
              <Button
                size="sm"
                variant="outline"
                onClick={() => void run(() => rpc.booroChief.ensure())}
              >
                <Crown />
                <Trans>Appoint the Chief</Trans>
              </Button>
            )}
            <NewMenu snapshot={snapshot} scope={scope} onOpen={setDialog} />
          </div>
        </div>
      ) : null}
      {shownError ? (
        <p role="alert" className="px-4 py-2 text-sm text-destructive">
          {shownError}
        </p>
      ) : null}
      <div className="relative min-h-0 flex-1">
        {notice ? (
          <p
            role="status"
            className="absolute start-1/2 top-3 z-10 -translate-x-1/2 rounded-full border border-border bg-card px-3 py-1.5 text-[13px] text-foreground shadow-sm"
          >
            {notice}
          </p>
        ) : null}
        {!snapshot ? null : agents.length === 0 && snapshot.groups.length === 0 ? (
          <p className="px-4 py-4 text-sm text-muted-foreground">
            <Trans>No agents yet</Trans>
          </p>
        ) : mode === "canvas" ? (
          <TeamCanvas
            snapshot={snapshot}
            scope={scope}
            lens={lens}
            readers={lens === "knowledge" ? readers : null}
            actions={actions}
          />
        ) : (
          <TeamList
            snapshot={snapshot}
            scope={scope}
            lens={lens}
            readers={lens === "knowledge" ? readers : null}
            actions={actions}
          />
        )}
      </div>
      {selected ? (
        <AgentDetailPanel
          botId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => void reload()}
        />
      ) : null}
      {dialog && snapshot ? (
        <GroupDialog
          state={dialog}
          snapshot={snapshot}
          onClose={() => setDialog(null)}
          onDone={async (change) => {
            await run(change);
            setDialog(null);
          }}
        />
      ) : null}
    </>
  );
}

/** The knowledge lens: one page, and the agents that may read it. */
function useKnowledgeLens(active: boolean) {
  const [pages, setPages] = useState<{ id: string; title: string }[]>([]);
  const [pageId, setPageId] = useState("");
  const [readers, setReaders] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (!active) return;
    let alive = true;
    void rpc.booroKnowledge
      .pages()
      .then((next) => {
        if (alive) setPages(next.map((page) => ({ id: page.id, title: page.title })));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [active]);
  useEffect(() => {
    if (!active || !pageId) {
      setReaders(null);
      return;
    }
    let alive = true;
    void rpc.booroKnowledge
      .access({ pageId })
      .then((access) => {
        if (alive) setReaders(new Set(access.readerIds));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [active, pageId]);
  return { pages, pageId, setPageId, readers };
}

/** Creates, renames, leads or removes a group. */
function GroupDialog({
  state,
  snapshot,
  onClose,
  onDone,
}: {
  state: NonNullable<GroupDialogState>;
  snapshot: OrgSnapshot;
  onClose: () => void;
  onDone: (change: () => Promise<unknown>) => Promise<void>;
}) {
  const { t } = useLingui();
  const making = state.mode === "create" || state.mode === "agent" || state.mode === "office";
  const group = state.mode === "lead" || state.mode === "remove" ? state.group : null;
  const [name, setName] = useState(
    state.mode === "agent" || state.mode === "office"
      ? ""
      : (group?.name ?? t({ message: "New group", context: "organigram" })),
  );
  const [role, setRole] = useState("");
  const [leadBotId, setLeadBotId] = useState(
    state.mode === "officeLead"
      ? (snapshot.offices.find((entry) => entry.id === state.spaceId)?.leadBotId ?? "")
      : (group?.leadBotId ?? ""),
  );
  const [busy, setBusy] = useState(false);
  const [office, setOffice] = useState(
    state.mode === "create" || state.mode === "agent" || state.mode === "officeLead"
      ? state.spaceId
      : (group?.spaceId ?? ""),
  );
  const chooseOffice =
    (state.mode === "create" || state.mode === "agent") &&
    state.chooseOffice === true &&
    snapshot.offices.length > 1;
  // A choice of another office starts at its top, outside any group.
  const parentGroupId =
    state.mode === "create" && office === state.spaceId ? state.parentGroupId : null;
  const agentGroupId = state.mode === "agent" && office === state.spaceId ? state.groupId : null;
  const spaceId = office;
  // The Chief leads the account and never leads a group.
  const officeAgents = snapshot.agents.filter(
    (agent) => agent.spaceId === spaceId && agent.id !== snapshot.chiefBotId,
  );

  const title =
    state.mode === "create"
      ? t({ message: "New group", context: "organigram" })
      : state.mode === "agent"
        ? t`New agent`
        : state.mode === "office"
          ? t`New office`
          : state.mode === "lead" || state.mode === "officeLead"
            ? t`Choose lead`
            : t`Remove ${group?.name ?? ""}?`;

  async function submit() {
    setBusy(true);
    try {
      if (state.mode === "create") {
        // Each new group gets the next colour, so groups are told apart from the start;
        // Pearl is left to the person, it barely tints a group.
        const tinted = GROUP_COLORS.slice(1);
        const color = tinted[snapshot.groups.length % tinted.length] ?? null;
        await onDone(() =>
          rpc.booroOrg.createGroup({
            spaceId,
            parentGroupId,
            name: name.trim(),
            color,
          }),
        );
      } else if (state.mode === "agent") {
        await onDone(async () => {
          // The agent is created in its office, then joins the group it was added to.
          const bot = await officeRpc(spaceId).bots.create({
            name: name.trim(),
            title: role.trim(),
          });
          if (agentGroupId) {
            await rpc.booroOrg.placeAgent({ botId: bot.id, groupId: agentGroupId });
          }
        });
      } else if (state.mode === "office") {
        await onDone(() => rpc.spaces.create({ name: name.trim() }));
      } else if (state.mode === "lead" && group) {
        await onDone(() =>
          rpc.booroOrg.updateGroup({ groupId: group.id, leadBotId: leadBotId || null }),
        );
      } else if (state.mode === "officeLead") {
        await onDone(() =>
          rpc.booroOrg.leadOffice({ spaceId: state.spaceId, leadBotId: leadBotId || null }),
        );
      } else if (state.mode === "remove" && group) {
        await onDone(() => rpc.booroOrg.removeGroup({ groupId: group.id }));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {chooseOffice ? (
            <NativeSelect
              aria-label={t`Office`}
              className="w-full"
              value={office}
              onChange={(event) => setOffice(event.target.value)}
            >
              {snapshot.offices.map((entry) => (
                <NativeSelectOption key={entry.id} value={entry.id}>
                  {entry.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          ) : null}
          {making ? (
            <Input
              autoFocus
              aria-label={t`Name`}
              placeholder={state.mode === "create" ? undefined : t`Name`}
              value={name}
              maxLength={GROUP_NAME_MAX_LENGTH}
              onFocus={(event) => event.target.select()}
              onChange={(event) => setName(event.target.value)}
            />
          ) : null}
          {state.mode === "agent" ? (
            <Input
              aria-label={t`Role`}
              placeholder={t`Role, e.g. finds new leads`}
              value={role}
              maxLength={200}
              onChange={(event) => setRole(event.target.value)}
            />
          ) : null}
          {state.mode === "lead" || state.mode === "officeLead" ? (
            <NativeSelect
              aria-label={t`Lead`}
              className="w-full"
              value={leadBotId}
              onChange={(event) => setLeadBotId(event.target.value)}
            >
              <NativeSelectOption value="">{t`No lead`}</NativeSelectOption>
              {officeAgents.map((agent) => (
                <NativeSelectOption key={agent.id} value={agent.id}>
                  {agent.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          ) : null}
          {state.mode === "remove" ? (
            <p className="text-sm text-muted-foreground">
              <Trans>What is in it moves one level up.</Trans>
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="submit"
              variant={state.mode === "remove" ? "destructive" : "default"}
              disabled={busy || (making && !name.trim())}
            >
              {state.mode === "remove" ? <Trans>Remove</Trans> : <Trans>Save</Trans>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** What can be made from the toolbar: in the office or group the filter shows. */
function NewMenu({
  snapshot,
  scope,
  onOpen,
}: {
  snapshot: OrgSnapshot;
  scope: OrgScope;
  onOpen: (state: GroupDialogState) => void;
}) {
  const scopedGroup = snapshot.groups.find((group) => group.id === scope.groupId);
  const spaceId = scopedGroup?.spaceId ?? scope.spaceId ?? snapshot.currentSpaceId;
  const parentGroupId = scopedGroup?.id ?? null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button size="sm" />}>
        <Plus />
        <Trans>New</Trans>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuItem
          onClick={() =>
            onOpen({ mode: "agent", spaceId, groupId: parentGroupId, chooseOffice: true })
          }
        >
          <Plus />
          <Trans>Agent</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onOpen({ mode: "create", spaceId, parentGroupId, chooseOffice: true })}
        >
          <Users />
          <Trans context="organigram">Group</Trans>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onOpen({ mode: "office" })}>
          <Building />
          <Trans>Office</Trans>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type ListLens = {
  lens: TeamLens;
  readers: ReadonlySet<string> | null;
};

/**
 * The same organigram as a list: the Chief on top, each office with its
 * groups, each with its lead first. The filter narrows it and
 * the lenses read as on the canvas; every move is a choice, no mouse needed.
 */
function TeamList({
  snapshot,
  scope,
  actions,
  ...view
}: {
  snapshot: OrgSnapshot;
  scope: OrgScope;
  actions: TeamCanvasActions;
} & ListLens) {
  const { t } = useLingui();
  const chief = snapshot.agents.find((agent) => agent.id === snapshot.chiefBotId);
  const filtered = Boolean(scope.spaceId || scope.groupId || scope.botId);
  const shown = snapshot.agents.filter(
    (agent) => agent.id !== snapshot.chiefBotId && agentInScope(snapshot.groups, agent, scope),
  );
  const scopedGroups = scope.groupId ? groupWithDescendants(snapshot.groups, scope.groupId) : null;
  const leads = new Set(
    snapshot.groups.flatMap((group) => (group.leadBotId ? [group.leadBotId] : [])),
  );
  const leadFirst = (agents: OrgAgent[]) =>
    [...agents].sort((a, b) => Number(leads.has(b.id)) - Number(leads.has(a.id)));
  return (
    <div
      data-testid="team-list"
      className="rk-scroll h-full overflow-y-auto px-4 pt-4 pb-24 md:px-6"
    >
      {chief && !filtered ? (
        <section data-testid="team-list-chief" className="mb-6">
          <ul>
            <AgentRow
              agent={chief}
              depth={0}
              groups={[]}
              actions={actions}
              chief={snapshot.offices.length > 1 ? "every office" : "alone"}
              {...view}
            />
          </ul>
        </section>
      ) : null}
      {snapshot.offices.map((office) => {
        if (scope.spaceId && scope.spaceId !== office.id) return null;
        const groups = orderedGroups(snapshot.groups, office.id);
        const officeAgents = shown.filter((agent) => agent.spaceId === office.id);
        const direct = officeAgents.filter(
          (agent) => !agent.groupId || !groups.some(({ group }) => group.id === agent.groupId),
        );
        // With a group or an agent chosen, only the groups that hold something are shown.
        const visibleGroups = groups.filter(({ group }) =>
          scopedGroups
            ? scopedGroups.has(group.id)
            : scope.botId
              ? officeAgents.some((agent) =>
                  groupWithDescendants(snapshot.groups, group.id).has(agent.groupId ?? ""),
                )
              : true,
        );
        if ((scope.groupId || scope.botId) && officeAgents.length === 0) return null;
        return (
          <section key={office.id} className="mb-6">
            {/* With one office there is no office level to name. */}
            {snapshot.offices.length === 1 ? null : (
              <h2 className="mb-1 flex items-center gap-2 text-[14px] font-semibold text-foreground">
                <Building
                  size={15}
                  strokeWidth={1.8}
                  className="text-muted-foreground"
                  aria-hidden
                />
                <InlineName
                  value={office.name}
                  label={t`Rename office`}
                  onSave={(name) => actions.renameOffice(office.id, name)}
                />
                <AddMenu
                  spaceId={office.id}
                  groupId={null}
                  actions={actions}
                  label={t`Add to ${office.name}`}
                />
              </h2>
            )}
            <ul className="space-y-0.5">
              {leadFirst(direct).map((agent) => (
                <AgentRow
                  key={agent.id}
                  agent={agent}
                  depth={0}
                  groups={groups}
                  actions={actions}
                  {...view}
                />
              ))}
              {visibleGroups.map(({ group, depth }) => (
                <li key={group.id}>
                  <div
                    className="flex items-center gap-2 py-1.5 text-[13.5px] font-medium text-foreground"
                    style={{ paddingInlineStart: `${depth * 18}px` }}
                  >
                    {group.color ? (
                      <span
                        aria-hidden
                        className="size-2.5 rounded-full"
                        style={{ backgroundColor: group.color }}
                      />
                    ) : (
                      <Users size={14} className="text-muted-foreground" aria-hidden />
                    )}
                    <InlineName
                      value={group.name}
                      label={t`Rename ${group.name}`}
                      onSave={(name) => actions.renameGroup(group, name)}
                    />
                    <AddMenu
                      spaceId={group.spaceId}
                      groupId={group.id}
                      actions={actions}
                      label={t`Add to ${group.name}`}
                    />
                    <GroupMenu group={group} actions={actions} />
                  </div>
                  <ul className="space-y-0.5">
                    {leadFirst(officeAgents.filter((agent) => agent.groupId === group.id)).map(
                      (agent) => (
                        <AgentRow
                          key={agent.id}
                          agent={agent}
                          depth={depth + 1}
                          groups={groups}
                          actions={actions}
                          lead={group.leadBotId === agent.id}
                          {...view}
                        />
                      ),
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function AgentRow({
  agent,
  depth,
  groups,
  actions,
  chief,
  lead = false,
  lens,
  readers,
}: {
  agent: OrgAgent;
  depth: number;
  groups: { group: OrgGroup; depth: number }[];
  actions: TeamCanvasActions;
  /** The Chief, which leads every office or, with one office, simply stands on top. */
  chief?: "every office" | "alone";
  lead?: boolean;
} & ListLens) {
  const { t } = useLingui();
  const lit = lens === "knowledge" && readers?.has(agent.id) === true;
  const dimmed = lens === "knowledge" && readers !== null && !readers.has(agent.id);
  let subline: React.ReactNode = chief
    ? chief === "every office"
      ? t`Leads every office`
      : ""
    : [lead ? t`Lead` : "", agent.title].filter(Boolean).join(" · ");
  if (lens === "knowledge" && readers?.has(agent.id)) subline = t`Reads the page`;
  return (
    <li
      data-testid={`team-row-${agent.id}`}
      className={`flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-accent ${
        lit ? "bg-accent ring-2 ring-primary/40" : ""
      } ${dimmed ? "opacity-40" : ""}`}
      style={{ paddingInlineStart: `${8 + depth * 18}px` }}
    >
      <button
        type="button"
        onClick={() => actions.openAgent(agent)}
        className="flex min-w-0 flex-1 items-center gap-3 text-start"
      >
        <BotAvatar color={agent.color} identity={agent.id} size={28} status={agent.status} />
        <span className="min-w-0">
          <span className="flex items-center gap-2">
            {chief ? <Crown size={14} className="shrink-0 text-foreground" aria-hidden /> : null}
            <InlineName
              value={agent.name}
              label={t`Rename ${agent.name}`}
              onSave={(name) => actions.renameAgent(agent, name)}
              className="text-[14px] font-medium text-foreground"
            />
            {lens === "status" ? <AgentLightDot light={agent.light} /> : null}
            {agent.hasOwnComputer ? <OwnComputerMark /> : null}
          </span>
          {subline ? (
            <span className="block truncate text-[12.5px] text-muted-foreground" dir="auto">
              {subline}
            </span>
          ) : null}
        </span>
      </button>
      {groups.length > 0 ? (
        <NativeSelect
          size="sm"
          aria-label={t`Move ${agent.name} to`}
          value={agent.groupId ?? ""}
          onChange={(event) => void actions.placeAgent(agent, event.target.value || null)}
        >
          <NativeSelectOption value="">{t`No group`}</NativeSelectOption>
          {groups.map(({ group, depth: level }) => (
            <NativeSelectOption key={group.id} value={group.id}>
              {`${" ".repeat(level)}${group.name}`}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      ) : null}
    </li>
  );
}
